import * as MicrosoftGraph from '@microsoft/microsoft-graph-types';
import { CompanyService, MsalService } from '.';
import * as Bixby from '$/core';
import { v4 as uuidv4 } from 'uuid';
import { PlannerService } from './PlannerService';
import { pluralizeStr } from '$/core/utils';
import dayjs from 'dayjs';

export abstract class PlannerNewsService extends PlannerService {
  public static async addPublicCompanyTaskAsync(model: Bixby.Models.CreatePublicCompanyTask): Promise<void> {
    const token: string | null = await MsalService.getPlannerToken();

    if (!token || token.length <= 0) {
      throw new Error('Token not found');
    }

    const bucketId = Bixby.Configuration.plannerDataSiteEmailBucketId;

    const title = `${model.bixbyId} (${model.timeFromEmail})`;

    let createPlannerTaskResponse: MicrosoftGraph.PlannerTask;
    try {
      // Create Task
      const createPlannerTask: MicrosoftGraph.PlannerTask = {
        planId: Bixby.Configuration.plannerDataSitePlanId,
        bucketId: bucketId,
        title: title,
        assignments: PlannerService.createAssignees(model),
      };
      createPlannerTaskResponse = await PlannerService.post<MicrosoftGraph.PlannerTask>(
        token,
        PlannerService.route,
        createPlannerTask
      );
    } catch (createError) {
      throw createError;
    }

    try {
      // Get Task Details
      const getPlannerTaskDetailsResponse: MicrosoftGraph.PlannerTaskDetails =
        await PlannerService.get<MicrosoftGraph.PlannerTaskDetails>(
          token,
          `${PlannerService.route}/${createPlannerTaskResponse.id}/details`
        );

      // Create Checklist
      const checklist: Bixby.Models.ChecklistItem[] = [];
      checklist.push({
        id: uuidv4(),
        title: PlannerService.sanitizeCheckListItemValue(`1) COMPANY: ${model.companyName}`),
      });
      checklist.push({
        id: uuidv4(),
        title: PlannerService.sanitizeCheckListItemValue(`2) BIXBY ID: ${model.bixbyId}`),
      });
      const initializedChecklist = PlannerService.initializeChecklist(checklist);

      await PlannerService.updateTaskDetails(
        createPlannerTaskResponse.id,
        token,
        (getPlannerTaskDetailsResponse as any)['@odata.etag'],
        initializedChecklist
      );
    } catch (updateError) {
      try {
        // Try to delete task
        const getTaskResponse: MicrosoftGraph.PlannerTask = await PlannerService.get<MicrosoftGraph.PlannerTask>(
          token,
          `${PlannerService.route}/${createPlannerTaskResponse.id}`
        );

        const deletePlannerTaskHeaders: Headers = new Headers();
        deletePlannerTaskHeaders.append('If-Match', (getTaskResponse as any)['@odata.etag']); // TODO - odata.etag TypeScript?
        await PlannerService.delete(token, `${PlannerService.route}/${getTaskResponse.id}`, deletePlannerTaskHeaders);
      } catch (deleteError) {
        // TODO - unable to delete task
      }

      // Throw
      throw updateError;
    }
  }

  public static async addClientCompanyTaskAsync(model: Bixby.Models.CreateClientCompanyTask): Promise<void> {
    const token: string | null = await MsalService.getPlannerToken();

    if (!token || token.length <= 0) {
      throw new Error('Token not found');
    }

    const isMissingEmail = model.taskType === Bixby.Enums.ClientCompanyTaskType.MissingEmail;
    const isEmail = model.taskType === Bixby.Enums.ClientCompanyTaskType.Email;
    const isRogue = model.taskType === Bixby.Enums.ClientCompanyTaskType.Rogue;

    if (isMissingEmail === false && isEmail === false && isRogue === false) {
      throw new Error('Invalid task type');
    }

    let bucketId = '';
    if (isEmail)
    {
      if (!model.hasPrivateDeclaration)
        bucketId = Bixby.Configuration.plannerDataSiteEmailBucketId;
      else
        bucketId = Bixby.Configuration.plannerMnpiDataSiteEmailBucketId;
    }
    else if (isRogue)
    {
      if (!model.hasPrivateDeclaration)
        bucketId = Bixby.Configuration.plannerDataSiteRogueBucketId;
      else
        bucketId = Bixby.Configuration.plannerMnpiDataSiteRogueBucketId;
    }
    else
    {
      if (!model.hasPrivateDeclaration)
        bucketId = Bixby.Configuration.plannerDataSiteMissingEmailBucketId;
      else
        bucketId = Bixby.Configuration.plannerMnpiCallTranscriptNeedsApprovalBucketId;
    }

    let planId = '';
    if (!model.hasPrivateDeclaration)
      planId = Bixby.Configuration.plannerDataSitePlanId;
    else
      planId = Bixby.Configuration.plannerMnpiDataSitePlanId;

    let title: string = isEmail
      ? `${model.dataSiteFromEmail} (${model.timeFromEmail})`
      : isRogue
      ? `(Rogue Post - ${model.client}) ${model.dataSiteSource}`
      : '';
    
    if(isMissingEmail) {
      title = `(Missing Email - ${model.client}) ${model.dataSiteSource}`;
    }

    let createPlannerTaskResponse: MicrosoftGraph.PlannerTask;
    try {
      // Create Task
      const createPlannerTask: MicrosoftGraph.PlannerTask = {
        planId: planId,
        bucketId: bucketId,
        title: title,
        assignments: PlannerService.createAssignees(model),
      };
      createPlannerTaskResponse = await PlannerService.post<MicrosoftGraph.PlannerTask>(
        token,
        PlannerService.route,
        createPlannerTask
      );
    } catch (createError) {
      throw createError;
    }

    try {
      // Get Task Details
      const getPlannerTaskDetailsResponse: MicrosoftGraph.PlannerTaskDetails =
        await PlannerService.get<MicrosoftGraph.PlannerTaskDetails>(
          token,
          `${PlannerService.route}/${createPlannerTaskResponse.id}/details`
        );

      // Create Checklist
      const checklist = PlannerNewsService.createChecklist(model);
      const initializedChecklist = PlannerService.initializeChecklist(checklist);
      const notes = PlannerNewsService.createNotes(model);

      await PlannerService.updateTaskDetails(
        createPlannerTaskResponse.id,
        token,
        (getPlannerTaskDetailsResponse as any)['@odata.etag'],
        initializedChecklist,
        notes
      );

      // Update Checklist Order
      await PlannerService.updateChecklistOrder(createPlannerTaskResponse.id, token);
    } catch (updateError) {
      try {
        // Try to delete task
        const getTaskResponse: MicrosoftGraph.PlannerTask = await PlannerService.get<MicrosoftGraph.PlannerTask>(
          token,
          `${PlannerService.route}/${createPlannerTaskResponse.id}`
        );

        const deletePlannerTaskHeaders: Headers = new Headers();
        deletePlannerTaskHeaders.append('If-Match', (getTaskResponse as any)['@odata.etag']); // TODO - odata.etag TypeScript?
        await PlannerService.delete(token, `${PlannerService.route}/${getTaskResponse.id}`, deletePlannerTaskHeaders);
      } catch (deleteError) {
        // TODO - unable to delete task
      }
      // Throw
      throw updateError;
    }
    
    // Store task in db
    try {

      const assignedTo = await Promise.all(model.assignees.map(async _ => {
        const user = await PlannerNewsService.getUserById(token, _);
        return user?.displayName || '';
      }))
      
      const createdBy = await PlannerNewsService.getUserById(token, createPlannerTaskResponse.createdBy?.user?.id);

      // Create Task request
      const request: Bixby.Models.CompanyTaskCreationRequest = {
        taskId: createPlannerTaskResponse.id || '',
        companyId: model.companyId,
        dateAssigned: dayjs.utc(model.timeFromEmail).toDate(),
        taskType: model.taskType,
        assignedTo:  assignedTo.join(", "), 
        createdBy: createdBy?.displayName || '',
        clientName: model.client
      };

      await CompanyService.createCompanyTask(request);

    } catch (updateError) {
      try {
        // If fails, Try to delete task
        const getTaskResponse: MicrosoftGraph.PlannerTask = await PlannerService.get<MicrosoftGraph.PlannerTask>(
          token,
          `${PlannerService.route}/${createPlannerTaskResponse.id}`
        );

        const deletePlannerTaskHeaders: Headers = new Headers();
        deletePlannerTaskHeaders.append('If-Match', (getTaskResponse as any)['@odata.etag']); // TODO - odata.etag TypeScript?
        await PlannerService.delete(token, `${PlannerService.route}/${getTaskResponse.id}`, deletePlannerTaskHeaders);
      } catch (deleteError) {
        // TODO - unable to delete task
      }
      
      // Throw
      throw updateError;
    }
  }

  private static async getUserById(
    token: string,
    userId: string | null | undefined
  ): Promise<MicrosoftGraph.User | null> {
    if (!userId || !token) return null;
    const userResponse: any = await PlannerService.get(token, `/users/${userId}`);
    return userResponse;
  }

  private static createChecklist: (model: Bixby.Models.CreateClientCompanyTask) => Bixby.Models.ChecklistItem[] = (
    model: Bixby.Models.CreateClientCompanyTask
  ) => {
    const isEmail = model.taskType === Bixby.Enums.ClientCompanyTaskType.Email;
    const isRogue = model.taskType === Bixby.Enums.ClientCompanyTaskType.Rogue;
    const isMissingEmail = model.taskType === Bixby.Enums.ClientCompanyTaskType.MissingEmail;

    let counter = 1;
    const checklistItems: Bixby.Models.ChecklistItem[] = [];

    checklistItems.push({
      id: uuidv4(),
      title: PlannerService.sanitizeCheckListItemValue(`${counter++}) ${model.client}`),
    });
    checklistItems.push({
      id: uuidv4(),
      title: PlannerService.sanitizeCheckListItemValue(`${counter++}) DATA SITE SOURCE: ${model.dataSiteSource}`),
    });
    checklistItems.push({
      id: uuidv4(),
      title: PlannerService.sanitizeCheckListItemValue(`${counter++}) COMPANY NAME: ${model.companyName}`),
    });
    checklistItems.push({
      id: uuidv4(),
      title: PlannerService.sanitizeCheckListItemValue(`${counter++}) BixbyID: ${model.bixbyId}`),
    });
    checklistItems.push({
      id: uuidv4(),
      title: PlannerService.sanitizeCheckListItemValue(`${counter++}) ${model.dataSiteProvider}`),
    });
    checklistItems.push({
      id: uuidv4(),
      title: PlannerService.sanitizeCheckListItemValue(`${counter++}) ${model.dataSiteType}`),
    });
    checklistItems.push({
      id: uuidv4(),
      title: PlannerService.sanitizeCheckListItemValue(`${counter++}) ${model.clientAccess}`),
    });

    if (isEmail) {
      checklistItems.push({
        id: uuidv4(),
        title: PlannerService.sanitizeCheckListItemValue(
          `${counter++}) Total Client Emails: ${model.totalClientEmails || ''}`
        ),
      });

      checklistItems.push({
        id: uuidv4(),
        title: PlannerService.sanitizeCheckListItemValue(
          `${counter++}) Verified emails for ${model.totalClientEmails} ${pluralizeStr(
            model.totalClientEmails,
            'client'
          )} (see notes)`
        ),
      });
    } else if (isRogue) {
      checklistItems.push({
        id: uuidv4(),
        title: PlannerService.sanitizeCheckListItemValue(
          `${counter++}) Verified rogue post doc(s) and file size(s) (see notes)`
        ),
      });
    } else if (isMissingEmail) {
      checklistItems.push({
        id: uuidv4(),
        title: PlannerService.sanitizeCheckListItemValue(
          `${counter++}) Verified missing email doc(s) and file size(s) (see notes)`
        ),
      });
    }

    checklistItems.push({ id: uuidv4(), title: PlannerService.sanitizeCheckListItemValue(`${counter++}) xx docs`) });
    checklistItems.push({
      id: uuidv4(),
      title: PlannerService.sanitizeCheckListItemValue(`${counter++}) Download - documents`),
    });
    checklistItems.push({
      id: uuidv4(),
      title: PlannerService.sanitizeCheckListItemValue(`${counter++}) Download - data site excel file`),
    });

    if (isEmail) {
      if (model.isEmailOnly) {
        checklistItems.push({
          id: uuidv4(),
          title: PlannerService.sanitizeCheckListItemValue(`${counter++}) Email only?`),
        });
        checklistItems.push({
          id: uuidv4(),
          title: PlannerService.sanitizeCheckListItemValue(`${counter++}) Email deleted from portal`),
        });
        checklistItems.push({
          id: uuidv4(),
          title: PlannerService.sanitizeCheckListItemValue(`${counter++}) Assigner confirms deletion`),
        });
        checklistItems.push({
          id: uuidv4(),
          title: PlannerService.sanitizeCheckListItemValue(`${counter++}) Upload docs to portal`),
        });
      }
    }
    checklistItems.push({
      id: uuidv4(),
      title: PlannerService.sanitizeCheckListItemValue(`${counter++}) Upload docs to portal`),
    });

    return checklistItems;
  };

  private static createNotes: (model: Bixby.Models.CreateClientCompanyTask) => string | undefined = (
    model: Bixby.Models.CreateClientCompanyTask
  ) => {
    const isEmail = model.taskType === Bixby.Enums.ClientCompanyTaskType.Email;
    const isRogue = model.taskType === Bixby.Enums.ClientCompanyTaskType.Rogue;
    const isMissingEmaili = model.taskType === Bixby.Enums.ClientCompanyTaskType.MissingEmail;
    if (isEmail) {
      return model.assignerDocs
        .sort((a, b) => (a.client > b.client ? 1 : -1))
        .map(a => `- [ ] ${a.client} - ${a.emails || ''} email(s) - ${a.docs || ''} doc(s)`)
        .join(`\r\n`);
    } else if (isRogue || isMissingEmaili) {
      return model.assignerDocs
        .sort((a, b) => (a.client > b.client ? 1 : -1))
        .map(a => `- [ ] ${a.client} - ${a.docs || ''} doc(s) - ${a.fileSizes || ''} file size(s)`)
        .join(`\r\n`);
    }
  };
}
